import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import type { QueryKey } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { ArActivityTimelineItem } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import {
  useAddCustomerNote,
  useEditCustomerNote,
  useDeleteCustomerNote,
} from '@/api/rpc/hooks/accounts-receivable/useArCustomerComms';

interface CustomerCommsPopoverProps {
  customerId: string;
  customerName: string | null;
  lastContactAt: string | null;
  recentActivity: ArActivityTimelineItem[];
  /** Query keys to invalidate after adding a note (e.g., the comms summary). */
  invalidateKeys?: readonly QueryKey[];
  children: React.ReactNode;
}

function useTypeLabels(): Record<string, string> {
  const gt = useGT();
  return {
    invoice_created: gt('Invoice created'),
    invoice_sent: gt('Invoice sent'),
    statement_sent: gt('Statement sent'),
    payment_received: gt('Payment received'),
    email_inbound: gt('Email received'),
    email_outbound: gt('Email sent'),
    credit_limit_change: gt('Credit limit changed'),
    customer_note_added: gt('Note'),
    collection_status_changed: gt('Status changed'),
    remittance_type_changed: gt('Remit type changed'),
  };
}

function relativeTime(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export function CustomerCommsPopover({
  customerId,
  customerName,
  lastContactAt,
  recentActivity,
  invalidateKeys = [],
  children,
}: CustomerCommsPopoverProps) {
  const gt = useGT();
  const typeLabels = useTypeLabels();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [composing, setComposing] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const isInteracting = composing || editingId !== null;
  const addNote = useAddCustomerNote(invalidateKeys);
  const editNote = useEditCustomerNote(invalidateKeys);
  const deleteNote = useDeleteCustomerNote(invalidateKeys);

  function handleAddNote() {
    const trimmed = noteDraft.trim();
    if (!trimmed) return;
    addNote.mutate(
      { customerId, note: trimmed },
      {
        onSuccess: () => {
          showToast({ color: 'success', message: gt('Note added') });
          setNoteDraft('');
          setComposing(false);
        },
        onError: (err) => {
          showToast({
            color: 'error',
            message:
              err instanceof Error ? err.message : gt('Failed to add note'),
          });
        },
      },
    );
  }

  function handleStartEdit(item: ArActivityTimelineItem) {
    setEditingId(item.id);
    setEditDraft(item.description);
  }

  function handleSaveEdit() {
    if (!editingId) return;
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    editNote.mutate(
      { eventId: editingId, note: trimmed },
      {
        onSuccess: () => {
          showToast({ color: 'success', message: gt('Note updated') });
          setEditingId(null);
          setEditDraft('');
        },
        onError: (err) => {
          showToast({
            color: 'error',
            message:
              err instanceof Error ? err.message : gt('Failed to update note'),
          });
        },
      },
    );
  }

  function handleDelete(eventId: string) {
    deleteNote.mutate(
      { eventId },
      {
        onSuccess: () => {
          showToast({ color: 'success', message: gt('Note deleted') });
        },
        onError: (err) => {
          showToast({
            color: 'error',
            message:
              err instanceof Error ? err.message : gt('Failed to delete note'),
          });
        },
      },
    );
  }

  const isNoteType = (type: string) => type === 'customer_note_added';

  return (
    <HoverCard
      openDelay={250}
      closeDelay={120}
      open={open}
      onOpenChange={(next) => {
        if (!next && isInteracting) return;
        setOpen(next);
      }}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className="w-80 p-3 space-y-2"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <div className="font-medium text-sm truncate">
            {customerName ?? gt('(Unknown)')}
          </div>
          <div className="text-xs text-muted-foreground">
            {lastContactAt ? relativeTime(lastContactAt) : gt('No activity')}
          </div>
        </div>
        <div className="border-t -mx-3" />
        {recentActivity.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2">
            <T>No recent activity</T>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {recentActivity.map((item) => (
              <li key={item.id} className="text-xs leading-snug">
                {editingId === item.id ? (
                  <div className="space-y-1.5">
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={2}
                      autoFocus
                      className="text-xs"
                    />
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs px-2"
                        onClick={() => setEditingId(null)}
                        disabled={editNote.isPending}
                      >
                        <T>Cancel</T>
                      </Button>
                      <Button
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={handleSaveEdit}
                        disabled={editNote.isPending || !editDraft.trim()}
                      >
                        <T>Save</T>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium">
                        {typeLabels[item.type] ?? item.type}
                      </span>
                      <span className="text-muted-foreground text-[10px] shrink-0">
                        {relativeTime(item.timestamp)}
                      </span>
                    </div>
                    {item.actorUserName && (
                      <div className="text-muted-foreground text-[10px]">
                        {item.actorUserName}
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-1">
                      <div className="text-muted-foreground truncate">
                        {item.description}
                      </div>
                      {isNoteType(item.type) && (
                        <div className="flex shrink-0 gap-0.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5"
                            onClick={() => handleStartEdit(item)}
                          >
                            <PencilSimpleIcon className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(item.id)}
                            disabled={deleteNote.isPending}
                          >
                            <TrashIcon className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="border-t -mx-3" />
        {composing ? (
          <div className="space-y-2">
            <Textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder={gt('Log a call, dispute, promise to pay…')}
              rows={3}
              autoFocus
              className="text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setComposing(false);
                  setNoteDraft('');
                }}
                disabled={addNote.isPending}
              >
                <T>Cancel</T>
              </Button>
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={addNote.isPending || !noteDraft.trim()}
              >
                <T>Save note</T>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <Link
              to="/accounts-receivable/collections/$customerId"
              params={{ customerId }}
              className="text-xs text-blue-600 hover:underline"
            >
              <T>View profile</T>
            </Link>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setComposing(true)}
            >
              <T>+ Note</T>
            </Button>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
